import { useEffect, useMemo, useRef } from 'react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { getSelectedPeriodUsingDates } from '@frontend/analytics-shared';
import { generateTrackingId, useCallIntelNavigator } from '@frontend/call-intel-core';
import { pendo, sentry } from '@frontend/tracking';
import { AnySearch, useSearch } from '@frontend/weave-router';
import { CALL_INTEL_TIME_PERIODS, DEFAULT_CALL_INTEL_TIME_PERIOD, VALID_CALL_INTEL_TABS } from '../../constants';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { useCallIntelLocations } from '../../hooks/use-call-intel-locations';
import { useCallIntelShallowStore } from '../../hooks/use-call-intel-store';
import { FilterValues } from '../types';

export const useInitCallIntelFilters = ({ checkURLFilters }: { checkURLFilters?: boolean } = {}) => {
  const { filters: storeFilters, allSelectedLocationIds, updateFilterValues: updateStoreFilters } = useActiveFilters();
  const search = useSearch({ strict: false });
  const { clearDeepLinkParamsWithMailerRedirect, hasDeepLinkParams } = useCallIntelNavigator();

  const { setActiveTab, setSubView, setUrlOverrideFilters } = useCallIntelShallowStore(
    'setActiveTab',
    'setSubView',
    'setUrlOverrideFilters'
  );

  const { preselectedAccessibleLocations } = useCallIntelLocations();

  const defaultLocations = useMemo(
    () => Object.keys(preselectedAccessibleLocations || {}),
    [preselectedAccessibleLocations]
  );

  const { startDate: defaultStartDate, endDate: defaultEndDate } =
    CALL_INTEL_TIME_PERIODS[DEFAULT_CALL_INTEL_TIME_PERIOD]?.value || {};

  const hasRecalculatedDatesRef = useRef(false);

  // Update default locations filter
  useEffect(() => {
    if (!allSelectedLocationIds.length && defaultLocations.length) {
      updateStoreFilters({ locations: defaultLocations });
    }
  }, [allSelectedLocationIds, defaultLocations, updateStoreFilters]);

  // Update default time period filter
  useEffect(() => {
    if (!storeFilters.startDate && !storeFilters.endDate) {
      updateStoreFilters({
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        selectedTimePeriodKey: DEFAULT_CALL_INTEL_TIME_PERIOD,
      });
    }
  }, [defaultEndDate, defaultStartDate, storeFilters.endDate, storeFilters.startDate, updateStoreFilters]);

  const hasUrlSearchFilters = useMemo(() => !!search?.filters, [search]);

  useEffect(() => {
    if (!checkURLFilters) return;

    if (!hasDeepLinkParams(search)) return;

    if (search?.tab) {
      const searchedTab = search.tab as CallIntelTypes.CallIntelTab;
      const tab = VALID_CALL_INTEL_TABS.includes(searchedTab) ? searchedTab : 'overview';
      setActiveTab(tab);
    }

    if (search?.subViewType || search?.subViewId) {
      const subView = {
        type: search.subViewType as CallIntelTypes.CallIntelSubViewType,
        id: search.subViewId as CallIntelTypes.CallIntelSubViewId,
      };
      setSubView(subView);
    }

    if (search?.filters) {
      try {
        const decodedFiltersString = atob(decodeURIComponent(search.filters));
        const decodedFilters: FilterValues = JSON.parse(decodedFiltersString);

        if (decodedFilters.startDate && decodedFilters.endDate) {
          const selectedPeriod = getSelectedPeriodUsingDates(
            DEFAULT_CALL_INTEL_TIME_PERIOD,
            true,
            CALL_INTEL_TIME_PERIODS,
            {
              startDate: decodedFilters.startDate,
              endDate: decodedFilters.endDate,
            }
          );

          decodedFilters.selectedTimePeriodKey = selectedPeriod;
        }

        // Track email card clicks (mailer is only present for email campaigns)
        if (search?.mailer) {
          setActiveTab('records'); // All email card clicks should navigate to records tab
          pendo.track(generateTrackingId({ context: 'email-card-click' }), {
            mailer: search?.mailer ?? '',
            metric: (search as AnySearch)?.metric ?? '',
            appliedFilters: decodedFiltersString,
          });
        }

        // add locations to decoded filters if they are not present
        if (!decodedFilters.locations?.length && defaultLocations.length) {
          decodedFilters.locations = defaultLocations;
        }

        // set time period to default if startDate and endDate are not present in decoded filters
        if (!decodedFilters.startDate && !decodedFilters.endDate) {
          decodedFilters.startDate = defaultStartDate;
          decodedFilters.endDate = defaultEndDate;
          decodedFilters.selectedTimePeriodKey = DEFAULT_CALL_INTEL_TIME_PERIOD;
        }

        // Store URL-derived filters as ephemeral overrides — not persisted to localStorage.
        setUrlOverrideFilters(decodedFilters);
      } catch (error) {
        sentry.warn({
          team: 'analytics',
          topic: 'analytics',
          error: 'Failed to decode and parse encodedSearchFilters in useInitCallIntelFilters',
          addContext: { name: 'useInitCallIntelFilters', context: { search } },
        });
      }
    }

    // Clear deep link params after processing
    if (hasDeepLinkParams(search)) {
      clearDeepLinkParamsWithMailerRedirect(search);
    }
  }, [
    checkURLFilters,
    clearDeepLinkParamsWithMailerRedirect,
    search,
    setActiveTab,
    setSubView,
    updateStoreFilters,
    hasDeepLinkParams,
    defaultLocations,
    setUrlOverrideFilters,
    defaultStartDate,
    defaultEndDate,
  ]);

  // Recalculate dates from selectedTimePeriodKey on refresh.
  useEffect(() => {
    if (hasRecalculatedDatesRef.current || hasUrlSearchFilters) return;
    hasRecalculatedDatesRef.current = true;

    const periodKey = storeFilters.selectedTimePeriodKey;

    // Only recalculate for named time periods (not custom date ranges)
    if (periodKey && periodKey in CALL_INTEL_TIME_PERIODS && storeFilters.startDate && storeFilters.endDate) {
      const freshDates = CALL_INTEL_TIME_PERIODS[periodKey as keyof typeof CALL_INTEL_TIME_PERIODS]?.value;

      // check if freshDates are different from store filters dates before updating to avoid unnecessary updates
      if (freshDates?.startDate === storeFilters.startDate && freshDates?.endDate === storeFilters.endDate) {
        return;
      }

      if (freshDates?.startDate && freshDates?.endDate) {
        updateStoreFilters({
          startDate: freshDates.startDate,
          endDate: freshDates.endDate,
        });
      }
    }
  }, [
    hasUrlSearchFilters,
    storeFilters.endDate,
    storeFilters.selectedTimePeriodKey,
    storeFilters.startDate,
    updateStoreFilters,
  ]);
};
